import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Truck, Search, ShieldCheck, ShieldAlert, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { publicOrigin } from '../lib/native';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</div>
    <div className="text-sm text-slate-800 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{value || '—'}</div>
  </div>
);

const FmcsaCarrierPanel = () => {
  const [mode, setMode] = useState('dot');       // 'dot' | 'mc'
  const [dot, setDot] = useState('');            // holds DOT or MC value
  const [linked, setLinked] = useState(null);      // stored snapshot on the company
  const [preview, setPreview] = useState(null);    // fresh lookup result (not yet saved)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [history, setHistory] = useState([]);
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/company/fmcsa`, { withCredentials: true });
        if (res.data?.fmcsa) { setLinked(res.data.fmcsa); setDot(res.data.dot_number || ''); }
        setHistory(res.data?.history || []);
        setCompanyId(res.data?.company_id || null);
      } catch (e) { /* no linked carrier yet */ }
    })();
  }, []);

  const copyTrustLink = () => {
    const url = `${publicOrigin()}/carrier/${companyId}`;
    navigator.clipboard?.writeText(url);
    setToast({ kind: 'success', msg: 'Public trust link copied to clipboard.' });
    setTimeout(() => setToast(null), 4000);
  };

  const lookup = async () => {
    setError(''); setPreview(null);
    if (!/^\d+$/.test(dot.trim())) { setError(`Enter a numeric US ${mode.toUpperCase()} number.`); return; }
    setLoading(true);
    try {
      const path = mode === 'mc' ? `fmcsa/carrier-by-mc/${dot.trim()}` : `fmcsa/carrier/${dot.trim()}`;
      const res = await axios.get(`${API}/${path}`, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      const code = e.response?.status;
      setError(code === 404 ? `No carrier found for ${mode.toUpperCase()} #${dot.trim()}.`
        : code === 503 ? 'FMCSA lookup is currently unavailable. Try again later or enter details manually.'
        : (e.response?.data?.detail || 'Lookup failed.'));
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true); setToast(null);
    try {
      const body = { overwrite_profile: true };
      if (mode === 'mc') body.mc_number = dot.trim(); else body.dot_number = dot.trim();
      const res = await axios.post(`${API}/company/fmcsa/link`, body, { withCredentials: true });
      setLinked(res.data.fmcsa); setPreview(null);
      setToast({ kind: 'success', msg: 'Carrier saved to company profile.' });
    } catch (e) {
      setToast({ kind: 'error', msg: e.response?.data?.detail || 'Failed to save.' });
    } finally { setSaving(false); setTimeout(() => setToast(null), 4000); }
  };

  const Snapshot = ({ data, title }) => {
    const oos = data.out_of_service;
    return (
      <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="fmcsa-snapshot">
        <div className={`px-4 py-3 flex items-center gap-2 ${oos ? 'bg-red-50' : 'bg-green-50'}`}>
          {oos ? <ShieldAlert className="w-4 h-4 text-red-600" /> : <ShieldCheck className="w-4 h-4 text-green-600" />}
          <span className={`text-sm font-semibold ${oos ? 'text-red-700' : 'text-green-700'}`} style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="fmcsa-status">
            {data.operating_status}
          </span>
          {title && <span className="ml-auto text-[11px] uppercase tracking-widest text-slate-400">{title}</span>}
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Legal name" value={data.legal_name} />
          <Field label="DBA" value={data.dba_name} />
          <Field label="US DOT #" value={data.dot_number} />
          <Field label="Address" value={data.address} />
          <Field label="Phone" value={data.telephone} />
          <Field label="Operation" value={data.carrier_operation} />
          <Field label="Common authority" value={data.common_authority} />
          <Field label="Contract authority" value={data.contract_authority} />
          <Field label="Safety rating" value={data.safety_rating} />
          <Field label="Power units" value={data.total_power_units} />
          <Field label="Drivers" value={data.total_drivers} />
          <Field label="Last checked" value={data.checked_at ? new Date(data.checked_at).toLocaleString() : null} />
        </div>
        {data.insurance_issues?.length > 0 && (
          <div className="mx-4 mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2" data-testid="fmcsa-insurance-issues">
            <div className="text-xs font-bold text-amber-800 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Insurance attention needed</div>
            <ul className="mt-1 list-disc list-inside text-xs text-amber-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {data.insurance_issues.map((i, idx) => <li key={idx}>{i}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="fmcsa-panel">
      <div className="flex items-start gap-3 mb-1">
        <Truck className="w-5 h-5 text-[#002FA7] mt-0.5" strokeWidth={2} />
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Carrier Verification (FMCSA)</h2>
          <p className="text-xs text-slate-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Look up your carrier by US DOT number and save the official FMCSA snapshot to your company profile. We re-check operating status daily.
          </p>
        </div>
      </div>

      {toast && (
        <div data-testid="fmcsa-toast" className={`my-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 border ${toast.kind === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {toast.kind === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 mb-1">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Search by</span>
        {['dot', 'mc'].map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`fmcsa-mode-${m}`}
            onClick={() => { setMode(m); setPreview(null); setError(''); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${mode === m ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {m === 'dot' ? 'US DOT #' : 'MC / Docket #'}
          </button>
        ))}
      </div>

      <div className="flex items-end gap-3 mt-2">
        <div className="flex-1 max-w-xs">
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{mode === 'mc' ? 'MC / Docket number' : 'US DOT number'}</label>
          <input
            data-testid="fmcsa-dot-input"
            value={dot}
            onChange={(e) => setDot(e.target.value.replace(/\D/g, ''))}
            placeholder={mode === 'mc' ? 'e.g. 1515' : 'e.g. 44110'}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          />
        </div>
        <button
          data-testid="fmcsa-lookup-button"
          onClick={lookup}
          disabled={loading || !dot}
          className="bg-[#002FA7] text-white px-4 py-2.5 rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50 flex items-center gap-2"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? 'Looking up…' : 'Look up'}
        </button>
      </div>

      {error && <div data-testid="fmcsa-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>}

      {preview && (
        <div className="mt-5 space-y-3">
          <Snapshot data={preview} title="Preview" />
          <div className="flex justify-end">
            <button
              data-testid="fmcsa-save-button"
              onClick={save}
              disabled={saving}
              className="bg-[#34C759] text-white px-4 py-2.5 rounded-md font-semibold hover:brightness-95 transition-all disabled:opacity-50 flex items-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save to company profile
            </button>
          </div>
        </div>
      )}

      {linked && !preview && (
        <div className="mt-5">
          <Snapshot data={linked} title="Saved" />
          {companyId && (
            <div className="mt-3 flex items-center gap-3">
              <button
                data-testid="fmcsa-copy-trust-link"
                onClick={copyTrustLink}
                className="text-sm font-semibold text-[#002FA7] hover:underline"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Copy public trust link →
              </button>
              <a
                href={`/carrier/${companyId}`}
                target="_blank"
                rel="noreferrer"
                data-testid="fmcsa-view-trust-link"
                className="text-sm text-slate-500 hover:text-slate-800"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Preview
              </a>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-5" data-testid="fmcsa-history">
          <h3 className="text-sm font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Operating-status history</h3>
          <ul className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {history.map((h, idx) => (
              <li key={idx} className="flex items-center justify-between px-3 py-2 text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <span className={`font-semibold ${h.out_of_service ? 'text-red-700' : 'text-green-700'}`}>{h.operating_status}{!h.insurance_ok ? ' · insurance issue' : ''}</span>
                <span className="text-slate-400">{h.at ? new Date(h.at).toLocaleString() : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FmcsaCarrierPanel;
